import type { ReactNode } from "react";

import { acceptDrop } from "../lib/dnd.js";
import { indentFor, isDescendant, orden, type Row } from "../lib/folderTree.js";
import type { MailFolder } from "../lib/types.js";
import { IconDots, iconForSpecialUse as iconFor } from "./icons.js";

/**
 * Wat de mappenlijst van het slepen moet weten. Gebundeld in één prop: los
 * doorgeven zou tien losse props opleveren en dan is niet meer te zien wat bij
 * elkaar hoort.
 */
export interface FolderDnd {
  /** false op mobiel; daar loopt alles via de mappenkiezer. */
  enabled: boolean;
  /** Wat er nú gesleept wordt, of null als er niets sleept. */
  kind: "messages" | "folder" | null;
  /** De bronmap (bij berichten) of de gesleepte map zelf. */
  sourcePath: string | null;
  /** Waar de muis nu boven hangt; null = de rij "hoogste niveau". */
  overPath: string | null | undefined;
  onOver: (target: MailFolder | null) => void;
  onLeave: () => void;
  onDropMessages: (target: MailFolder) => void;
  /** null = naar het hoogste niveau. */
  onDropFolder: (target: MailFolder | null) => void;
  onFolderDragStart: (folder: MailFolder, event: React.DragEvent) => void;
  onDragEnd: () => void;
}

interface FolderListProps {
  folders: MailFolder[] | null;
  activePath: string | null;
  error: string | null;
  onSelect: (folder: MailFolder) => void;
  dnd: FolderDnd;
  /** Aanwezig = elke gewone map krijgt een kebab met beheeracties. */
  onFolderMenu?: (folder: MailFolder, anchor: HTMLElement) => void;
}

/**
 * Mag hier gedropt worden?
 *
 * De weergavediepte uit `orden()` speelt hier bewust geen rol: die is
 * genormaliseerd voor het hijsen van speciale mappen en zegt niets over de
 * echte ouder-kindrelatie. Alles gaat op `path`.
 */
function canDrop(dnd: FolderDnd, folders: MailFolder[], target: MailFolder): boolean {
  if (!dnd.enabled || dnd.kind === null || dnd.sourcePath === null) return false;

  if (dnd.kind === "messages") {
    // Een \Noselect-container kan geen post bevatten, en verplaatsen naar de
    // map waar het al in zit is geen verplaatsing.
    return target.selectable && target.path !== dnd.sourcePath;
  }

  const source = folders.find((f) => f.path === dnd.sourcePath);
  if (source === undefined) return false;
  // Niet in zichzelf, niet in de eigen nakomelingen (dat zou de subboom
  // opeten), en niet in de map waar hij al onder hangt.
  if (target.path === source.path) return false;
  if (isDescendant(source, target.path)) return false;
  return !isDirectParent(source, target);
}

/** Ligt `source` al direct onder `target`? Dan is de drop een no-op. */
function isDirectParent(source: MailFolder, target: MailFolder): boolean {
  const prefix = `${target.path}${target.delimiter}`;
  if (!source.path.startsWith(prefix)) return false;
  return !source.path.slice(prefix.length).includes(source.delimiter);
}

function FolderRow({ row, active, dropTarget, canBeDropped, onSelect, dnd, onFolderMenu }: {
  row: Row;
  active: boolean;
  dropTarget: boolean;
  canBeDropped: boolean;
  onSelect: (folder: MailFolder) => void;
  dnd: FolderDnd;
  onFolderMenu: ((folder: MailFolder, anchor: HTMLElement) => void) | undefined;
}): ReactNode {
  const { folder, depth } = row;

  const dropProps = canBeDropped
    ? {
        onDragOver: (e: React.DragEvent) => {
          acceptDrop(e);
          dnd.onOver(folder);
        },
        onDrop: (e: React.DragEvent) => {
          e.preventDefault();
          if (dnd.kind === "messages") dnd.onDropMessages(folder);
          else dnd.onDropFolder(folder);
        },
      }
    : {};

  const className = [
    "folder-row",
    active ? "is-active" : "",
    dropTarget ? "is-drop-target" : "",
    !folder.selectable ? "folder-row--static" : "",
  ]
    .filter((part) => part !== "")
    .join(" ");

  // Standaardmappen blijven staan waar ze staan: hernoemen of verplaatsen
  // breekt de special-use-herkenning en daarmee waar verwijderde post heen gaat.
  const draggable = dnd.enabled && folder.specialUse === null && folder.selectable;

  return (
    <div
      className={className}
      style={{ paddingLeft: indentFor(depth) }}
      draggable={draggable}
      onDragStart={draggable ? (e) => dnd.onFolderDragStart(folder, e) : undefined}
      onDragEnd={draggable ? dnd.onDragEnd : undefined}
      {...dropProps}
    >
      {folder.selectable ? (
        <button
          type="button"
          className="folder-open"
          aria-current={active ? "true" : undefined}
          onClick={() => onSelect(folder)}
        >
          <span className="folder-icon">{iconFor(folder.specialUse)}</span>
          <span className="folder-name">{folder.name}</span>
          {folder.unseen > 0 && (
            <span className="folder-badge" aria-label={`${folder.unseen} ongelezen`}>
              {folder.unseen > 999 ? "999+" : folder.unseen}
            </span>
          )}
        </button>
      ) : (
        // \Noselect-container: bestaat alleen om submappen te dragen.
        <span className="folder-open folder-open--static">
          <span className="folder-icon">{iconFor(folder.specialUse)}</span>
          <span className="folder-name">{folder.name}</span>
        </span>
      )}

      {onFolderMenu !== undefined && folder.specialUse === null && (
        <button
          type="button"
          className="folder-more"
          aria-label={`Acties voor ${folder.name}`}
          aria-haspopup="menu"
          onClick={(e) => onFolderMenu(folder, e.currentTarget)}
        >
          <IconDots />
        </button>
      )}
    </div>
  );
}

export function FolderList({
  folders,
  activePath,
  error,
  onSelect,
  dnd,
  onFolderMenu,
}: FolderListProps): ReactNode {
  if (error !== null) {
    return <p className="pane-message pane-message--error">{error}</p>;
  }

  if (folders === null) {
    return (
      <ul className="folder-list" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((index) => (
          <li key={index} className="folder-row folder-row--skeleton">
            <span className="skeleton skeleton--text" />
          </li>
        ))}
      </ul>
    );
  }

  if (folders.length === 0) {
    return <p className="pane-message">Er zijn geen mappen gevonden.</p>;
  }

  const { special, rest } = orden(folders);

  function renderRow(row: Row): ReactNode {
    const canBeDropped = canDrop(dnd, folders ?? [], row.folder);
    return (
      <li key={row.folder.path}>
        <FolderRow
          row={row}
          active={row.folder.path === activePath}
          dropTarget={canBeDropped && dnd.overPath === row.folder.path}
          canBeDropped={canBeDropped}
          onSelect={onSelect}
          dnd={dnd}
          onFolderMenu={onFolderMenu}
        />
      </li>
    );
  }

  return (
    // Eén onDragLeave op de omhulling in plaats van per rij: bij rijen vuurt
    // dragleave ook bij het oversteken naar een kindelement, en dan knippert de
    // markering.
    <nav aria-label="Mappen" onDragLeave={dnd.onLeave}>
      <ul className="folder-list">
        {/* Alleen zichtbaar tijdens het slepen van een map; anders is het
            permanente rommel in de zijbalk. */}
        {dnd.kind === "folder" && (
          <li>
            <div
              className={`folder-row folder-drop-root${dnd.overPath === null ? " is-drop-target" : ""}`}
              onDragOver={(e) => {
                acceptDrop(e);
                dnd.onOver(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                dnd.onDropFolder(null);
              }}
            >
              <span className="folder-name">Bovenaan (geen bovenliggende map)</span>
            </div>
          </li>
        )}

        {special.map(renderRow)}

        {special.length > 0 && rest.length > 0 && (
          <li aria-hidden="true"><div className="folder-divider" /></li>
        )}

        {rest.map(renderRow)}
      </ul>
    </nav>
  );
}
